/**
 * ComposeProgressModal
 *
 * Step-by-step progress modal for the compose flow:
 * 1. Create session
 * 2. Pay composition fee (Xaman payment)
 * 3. Burn NFTs one by one (Xaman signing per burn)
 * 4. Wait for composition & minting
 * 5. Done!
 *
 * Supports "finish early" after at least 1 burn.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Button } from '../../ui/components/Button';
import { FeatherCheck, FeatherX, FeatherZap } from '@subframe/core';
import {
  createComposeSession,
  createFeePayload,
  createBurnPayload,
  reportFeePaid,
  reportBurn,
  finishComposeEarly,
  cancelComposeSession,
  getComposeSession,
  getIncompleteComposeSessions,
  type ComposeSession,
} from '../../api/compose';
import { WalletSigningModal } from '../transactions/WalletSigningModal';
import type { SigningPayload } from '../../types/signing';

type ComposeStep = 'creating' | 'paying' | 'burning' | 'composing' | 'done' | 'error' | 'stale_session';

interface ComposeProgressModalProps {
  launchId: string;
  nftTokenIds: string[];
  compositionFeeDrops: number;
  onComplete: () => void;
  onClose: () => void;
}

export const ComposeProgressModal: React.FC<ComposeProgressModalProps> = ({
  launchId,
  nftTokenIds,
  compositionFeeDrops,
  onComplete,
  onClose,
}) => {
  const [step, setStep] = useState<ComposeStep>('creating');
  const [session, setSession] = useState<ComposeSession | null>(null);
  const [currentBurnIndex, setCurrentBurnIndex] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [staleSession, setStaleSession] = useState<ComposeSession | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const sessionCreatedRef = useRef(false);

  // Xaman signing state
  const [xamanPayload, setXamanPayload] = useState<SigningPayload | null>(null);
  const [showSigning, setShowSigning] = useState(false);
  const [signingTitle, setSigningTitle] = useState('');
  const [signingLoading, setSigningLoading] = useState(false);
  const [paymentSubmitted, setPaymentSubmitted] = useState(false);

  // Determine the correct step for a resumed session
  const resumeSession = useCallback((s: ComposeSession) => {
    setSession(s);
    if (s.status === 'completed') {
      setStep('done');
    } else if (s.status === 'composing' || s.status === 'minting') {
      setStep('composing');
    } else if (s.status === 'pending_burns' || s.status === 'burning') {
      setStep('burning');
      setCurrentBurnIndex(0);
    } else if (s.status === 'pending_payment') {
      if (s.composition_fee_drops <= 0) {
        // Zero-fee session stuck in pending_payment — skip to burning
        setStep('burning');
        setCurrentBurnIndex(0);
      } else if (s.fee_payment_tx_hash) {
        // Payment submitted but awaiting on-chain verification
        setStep('paying');
        setPaymentSubmitted(true);
      } else {
        setStep('paying');
      }
    } else {
      // Unknown status, treat as error
      setError(`Session is in unexpected status: ${s.status}`);
      setStep('error');
    }
  }, []);

  // Step 1: Create session or resume existing (run once on mount)
  useEffect(() => {
    if (sessionCreatedRef.current) return;
    sessionCreatedRef.current = true;

    const createOrResume = async () => {
      try {
        // Check for existing incomplete session first
        const incomplete = await getIncompleteComposeSessions(launchId);
        if (incomplete.length > 0) {
          const existing = incomplete[0];
          // Only resume if the session's NFTs match what the user currently selected.
          // Resuming a stale session with different NFTs is dangerous — it could
          // burn NFTs the user didn't intend to include.
          const existingIds = new Set(existing.input_nft_token_ids);
          const currentIds = new Set(nftTokenIds);
          const matches =
            existingIds.size === currentIds.size &&
            nftTokenIds.every((id) => existingIds.has(id));

          if (matches) {
            resumeSession(existing);
            return;
          }

          // Stale session with different NFTs — offer cancel or resume
          setStaleSession(existing);
          setStep('stale_session');
          return;
        }

        const newSession = await createComposeSession(launchId, nftTokenIds);
        setSession(newSession);
        // Skip fee step when fee is 0
        if (compositionFeeDrops <= 0) {
          setStep('burning');
          setCurrentBurnIndex(0);
        } else {
          setStep('paying');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to create compose session');
        setStep('error');
      }
    };
    createOrResume();
  }, [launchId, nftTokenIds, compositionFeeDrops, resumeSession]);

  // Poll session status after fee payment submitted (waiting for on-chain verification)
  useEffect(() => {
    if (step !== 'paying' || !paymentSubmitted || !session) return;

    const interval = setInterval(async () => {
      try {
        const updated = await getComposeSession(launchId, session.id);
        setSession(updated);
        if (updated.status === 'pending_burns' || updated.status === 'burning') {
          setPaymentSubmitted(false);
          setStep('burning');
          setCurrentBurnIndex(0);
        }
      } catch {
        // Ignore polling errors
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [step, paymentSubmitted, session, launchId]);

  // Poll session status during composing/minting phase
  useEffect(() => {
    if (step !== 'composing' || !session) return;

    const interval = setInterval(async () => {
      try {
        const updated = await getComposeSession(launchId, session.id);
        setSession(updated);
        if (updated.status === 'completed') {
          setStep('done');
        }
      } catch {
        // Ignore polling errors
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [step, session, launchId]);

  // Step 2: Initiate fee payment signing
  const handlePayFee = useCallback(async () => {
    if (!session) return;
    setSigningLoading(true);
    setError(null);
    try {
      const payload = await createFeePayload(launchId, session.id);
      setXamanPayload(payload);
      setSigningTitle(`Pay Build Fee (${compositionFeeDrops / 1_000_000} XRP)`);
      setShowSigning(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create payment payload');
    } finally {
      setSigningLoading(false);
    }
  }, [session, launchId, compositionFeeDrops]);

  // Fee payment signed successfully — wait for on-chain verification before proceeding
  const handleFeeSuccess = useCallback(async (txid: string) => {
    if (!session) return;
    setShowSigning(false);
    setXamanPayload(null);
    try {
      const updated = await reportFeePaid(launchId, session.id, txid);
      setSession(updated);
      setPaymentSubmitted(true);
      // Don't advance to burning — polling will detect when verification completes
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to record fee payment');
    }
  }, [session, launchId]);

  // Use burns_remaining from session for correct resume behavior
  const burnsRemaining = session?.burns_remaining || [];
  const totalBurns = session?.input_nft_token_ids?.length || nftTokenIds.length;

  // Re-fetch session from the backend to stay in sync after burns
  const refreshSession = useCallback(async (): Promise<ComposeSession | null> => {
    if (!session) return null;
    try {
      const updated = await getComposeSession(launchId, session.id);
      setSession(updated);
      return updated;
    } catch {
      return null;
    }
  }, [session, launchId]);

  // Step 3: Initiate NFT burn signing
  const handleBurnNft = useCallback(async () => {
    if (!session || burnsRemaining.length === 0) return;
    const nftTokenId = burnsRemaining[currentBurnIndex];
    setSigningLoading(true);
    setError(null);
    try {
      const payload = await createBurnPayload(launchId, session.id, nftTokenId);
      setXamanPayload(payload);
      setSigningTitle(`Burn NFT ${(session.burns_completed?.length || 0) + currentBurnIndex + 1} of ${totalBurns}`);
      setShowSigning(true);
    } catch (err) {
      const msg = err instanceof Error ? err.message : '';
      // If the backend says the NFT isn't in the pending list, our session state is stale
      // (e.g. background processor already recorded the burn). Re-fetch to get back in sync.
      if (msg.includes('not in the pending burns list') || msg.includes('already confirmed')) {
        const updated = await refreshSession();
        if (updated) {
          if (updated.status === 'composing' || updated.status === 'minting' || updated.status === 'completed') {
            setStep(updated.status === 'completed' ? 'done' : 'composing');
          } else {
            // Session refreshed — reset index to 0 for the updated burns_remaining
            setCurrentBurnIndex(0);
          }
          return;
        }
      }
      setError(msg || 'Failed to create burn payload');
    } finally {
      setSigningLoading(false);
    }
  }, [session, launchId, currentBurnIndex, burnsRemaining, totalBurns, refreshSession]);

  // Burn signed successfully
  const handleBurnSuccess = useCallback(async (txid: string) => {
    if (!session || burnsRemaining.length === 0) return;
    const nftTokenId = burnsRemaining[currentBurnIndex];
    setShowSigning(false);
    setXamanPayload(null);
    try {
      await reportBurn(launchId, session.id, nftTokenId, txid);

      // Refresh session to stay in sync with backend (background processor
      // may have already recorded this burn or advanced the session status)
      const updated = await refreshSession();

      if (updated?.status === 'composing' || updated?.status === 'minting' || updated?.status === 'completed') {
        setStep(updated.status === 'completed' ? 'done' : 'composing');
        return;
      }

      // Use refreshed burns_remaining; reset index to 0 since the array shifted
      const remaining = updated?.burns_remaining || burnsRemaining;
      if (remaining.length === 0) {
        setStep('composing');
      } else {
        setCurrentBurnIndex(0);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to report burn');
    }
  }, [session, launchId, currentBurnIndex, burnsRemaining, refreshSession]);

  // Signing rejected or expired
  const handleSigningRejected = useCallback(() => {
    setShowSigning(false);
    setXamanPayload(null);
    setError('Transaction was rejected. You can try again.');
  }, []);

  const handleSigningExpired = useCallback(() => {
    setShowSigning(false);
    setXamanPayload(null);
    setError('Transaction request expired. You can try again.');
  }, []);

  const handleFinishEarly = useCallback(async () => {
    if (!session) return;
    try {
      const updated = await finishComposeEarly(launchId, session.id);
      setSession(updated);
      setStep('composing');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to finish early');
    }
  }, [session, launchId]);

  // Cancel a stale session (only when no burns have happened)
  const handleCancelStaleSession = useCallback(async () => {
    if (!staleSession) return;
    setCancelling(true);
    try {
      await cancelComposeSession(launchId, staleSession.id);
      setStaleSession(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel session');
      setStep('error');
      setCancelling(false);
      return;
    }
    try {
      const newSession = await createComposeSession(launchId, nftTokenIds);
      setSession(newSession);
      if (compositionFeeDrops <= 0) {
        setStep('burning');
        setCurrentBurnIndex(0);
      } else {
        setStep('paying');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Previous session cancelled. Failed to create new session — close and try again.');
      setStep('error');
    } finally {
      setCancelling(false);
    }
  }, [staleSession, launchId, nftTokenIds, compositionFeeDrops]);

  // Resume a stale session that has burns in progress
  const handleResumeStaleSession = useCallback(() => {
    if (!staleSession) return;
    resumeSession(staleSession);
    setStaleSession(null);
  }, [staleSession, resumeSession]);

  const burnsCompleted = session?.burns_completed?.length || 0;

  return (
    <>
      <div className={`fixed inset-0 z-50 flex items-center justify-center bg-neutral-900/50 transition-opacity ${showSigning ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
        <div className="bg-default-background rounded-xl shadow-2xl max-w-md w-full mx-4 p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-heading-3 font-heading-3 text-default-font">Building</h2>
            {step !== 'composing' && (
              <button onClick={onClose} className="p-1 text-subtext-color hover:text-default-font">
                <FeatherX className="w-5 h-5" />
              </button>
            )}
          </div>

          {/* Progress Steps */}
          <div className="space-y-4 mb-6">
            <ProgressItem
              label="Create Session"
              status={step === 'creating' ? 'active' : session ? 'done' : 'pending'}
            />
            {compositionFeeDrops > 0 && (
              <ProgressItem
                label={`Pay Build Fee (${compositionFeeDrops / 1_000_000} XRP)`}
                status={
                  step === 'paying' ? 'active' :
                  session?.fee_paid_at ? 'done' : 'pending'
                }
              />
            )}
            <ProgressItem
              label={`Burn NFTs (${burnsCompleted}/${totalBurns})`}
              status={
                step === 'burning' ? 'active' :
                step === 'composing' || step === 'done' ? 'done' : 'pending'
              }
            />
            <ProgressItem
              label="Build & Mint"
              status={
                step === 'composing' ? 'active' :
                step === 'done' ? 'done' : 'pending'
              }
            />
          </div>

          {/* Warning — shown during burn and composing steps */}
          {(step === 'burning' || step === 'composing') && (
            <div className="flex gap-2 p-3 rounded-lg bg-error-50 border border-error-200 mb-4">
              <FeatherZap className="w-4 h-4 text-error-600 flex-shrink-0 mt-0.5" />
              <p className="text-caption font-caption text-error-700">
                <strong>Do not leave this page.</strong> Your NFTs are being permanently burned. If you navigate away, your progress may be lost and burned NFTs cannot be recovered.
              </p>
            </div>
          )}

          {/* Action Area */}
          {step === 'paying' && (
            <div className="space-y-3">
              {paymentSubmitted ? (
                <>
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-brand-200 border-t-brand-600 rounded-full animate-spin" />
                    <p className="text-body font-body text-subtext-color">
                      Verifying payment on-chain...
                    </p>
                  </div>
                  <p className="text-caption font-caption text-subtext-color">
                    This usually takes a few seconds.
                  </p>
                </>
              ) : (
                <>
                  <p className="text-body font-body text-subtext-color">
                    Sign the payment transaction to pay the build fee.
                  </p>
                  {error && (
                    <p className="text-caption font-caption text-error-600">{error}</p>
                  )}
                  <Button
                    variant="brand-primary"
                    className="w-full"
                    onClick={handlePayFee}
                    disabled={signingLoading}
                  >
                    {signingLoading ? 'Creating payload...' : 'Sign Payment in Xaman'}
                  </Button>
                </>
              )}
            </div>
          )}

          {step === 'burning' && (
            <div className="space-y-3">
              <p className="text-body font-body text-subtext-color">
                Burn NFT {burnsCompleted + currentBurnIndex + 1} of {totalBurns}
              </p>
              <p className="text-caption font-caption text-subtext-color break-all">
                {burnsRemaining[currentBurnIndex]}
              </p>
              {error && (
                <p className="text-caption font-caption text-error-600">{error}</p>
              )}
              <Button
                variant="brand-primary"
                className="w-full"
                onClick={handleBurnNft}
                disabled={signingLoading}
              >
                {signingLoading ? 'Creating payload...' : 'Sign Burn in Xaman'}
              </Button>
              {burnsCompleted >= 1 && (
                <Button variant="neutral-secondary" className="w-full" onClick={handleFinishEarly}>
                  Finish Early ({burnsCompleted} burned)
                </Button>
              )}
            </div>
          )}

          {step === 'composing' && (
            <div className="text-center py-4">
              <div className="w-8 h-8 border-2 border-brand-200 border-t-brand-600 rounded-full animate-spin mx-auto mb-3" />
              <p className="text-body font-body text-subtext-color">
                Building your NFT and minting on-chain...
              </p>
              <p className="text-caption font-caption text-subtext-color mt-1">
                This may take a minute. Don't close this window.
              </p>
            </div>
          )}

          {step === 'done' && (
            <div className="text-center py-4">
              <div className="w-12 h-12 bg-success-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <FeatherCheck className="w-6 h-6 text-success-600" />
              </div>
              <p className="text-body-bold font-body-bold text-default-font mb-1">
                Build Complete!
              </p>
              <p className="text-body font-body text-subtext-color mb-4">
                Your new NFT has been minted. Check your wallet for the transfer offer.
              </p>
              <Button variant="brand-primary" className="w-full" onClick={onComplete}>
                Done
              </Button>
            </div>
          )}

          {step === 'stale_session' && staleSession && (
            <div className="space-y-4">
              {(staleSession.burns_completed?.length || 0) > 0 ? (
                <>
                  <p className="text-body font-body text-subtext-color">
                    You have an unfinished build with {staleSession.burns_completed!.length} NFT{staleSession.burns_completed!.length > 1 ? 's' : ''} already burned.
                    You should resume it to avoid losing those NFTs.
                  </p>
                  <Button variant="brand-primary" className="w-full" onClick={handleResumeStaleSession}>
                    Resume Previous Build
                  </Button>
                  <Button variant="neutral-secondary" className="w-full" onClick={onClose}>
                    Close
                  </Button>
                </>
              ) : (
                <>
                  <p className="text-body font-body text-subtext-color">
                    You have an unfinished build session with different NFTs. No NFTs have been burned yet, so you can safely cancel it and start fresh.
                  </p>
                  <Button
                    variant="brand-primary"
                    className="w-full"
                    onClick={handleCancelStaleSession}
                    disabled={cancelling}
                  >
                    {cancelling ? 'Cancelling...' : 'Cancel Previous & Start New Build'}
                  </Button>
                  <Button variant="neutral-secondary" className="w-full" onClick={handleResumeStaleSession}>
                    Resume Previous Build Instead
                  </Button>
                </>
              )}
            </div>
          )}

          {step === 'error' && (
            <div className="text-center py-4">
              <div className="w-12 h-12 bg-error-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <FeatherX className="w-6 h-6 text-error-600" />
              </div>
              <p className="text-body font-body text-error-600 mb-4">{error}</p>
              <Button variant="neutral-secondary" className="w-full" onClick={onClose}>
                Close
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Xaman Signing Modal — progress modal hides while this is open */}
      {xamanPayload && (
        <WalletSigningModal
          open={showSigning}
          onClose={() => {
            setShowSigning(false);
            setXamanPayload(null);
          }}
          title={signingTitle}
          payload={xamanPayload}
          onSuccess={step === 'paying' ? handleFeeSuccess : handleBurnSuccess}
          onRejected={handleSigningRejected}
          onExpired={handleSigningExpired}
        />
      )}
    </>
  );
};

// ==================== Sub-component ====================

interface ProgressItemProps {
  label: string;
  status: 'pending' | 'active' | 'done';
}

const ProgressItem: React.FC<ProgressItemProps> = ({ label, status }) => (
  <div className="flex items-center gap-3">
    <div
      className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
        status === 'done'
          ? 'bg-success-600'
          : status === 'active'
            ? 'bg-brand-600'
            : 'bg-neutral-200'
      }`}
    >
      {status === 'done' ? (
        <FeatherCheck className="w-3 h-3 text-white" />
      ) : status === 'active' ? (
        <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
      ) : (
        <div className="w-2 h-2 rounded-full bg-neutral-400" />
      )}
    </div>
    <span
      className={`text-body font-body ${
        status === 'done'
          ? 'text-success-700'
          : status === 'active'
            ? 'text-default-font font-bold'
            : 'text-subtext-color'
      }`}
    >
      {label}
    </span>
  </div>
);
